/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Location} from '@angular/common';
import {ChangeDetectionStrategy, Component, OnInit, Optional, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {NbTabComponent, NbTabsetComponent, NbWindowRef,} from '@nebular/theme';
import {ApiResponse, BreadcrumbDto, DamItem, DamItemVersion, DamRelatedCollection, FileService, Permission,} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {translations} from '../../../../../environments/translations.ru';
import {CreateUpdateComponent} from '../../../../admin-shared/crud-components/create-update/create-update.component';
import {CrudConfig} from '../../../../admin-shared/interfaces/crud.config';
import {FormConfigService} from '../../../../admin-shared/services/form-config.service';
import {DamService} from '../../dam.service';
import {DamItemVersionTypeCountWSelected} from '../dam-table/dam-catalog.component';
import {FILES_FORM} from '../files-forms.config';

@Component({
  selector: 'create-update-files',
  templateUrl: './create-update-files.component.html',
  styleUrls: ['./create-update-files.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateUpdateFilesComponent extends CreateUpdateComponent<DamItem> implements OnInit {
  @ViewChild('tabset') tabsetEl: NbTabsetComponent;
  @ViewChild('attachedElementsTab') attachedElementsTab: NbTabComponent;
  @ViewChild('featureTab') featureTabEl: NbTabComponent;
  catalogBreadCrumbs: Observable<BreadcrumbDto[]>;
  featureTabSpinner = false;
  title = translations.files;
  uri = '/dam';
  absolutePath = '/cabinet/dam';
  model: any;
  redirectToEditPage = true;
  modelFeaturesChanges = false;
  protected entity: any;
  config: CrudConfig = {
    uri: this.uri,
    columns: FILES_FORM,
    title: this.title,
    absolutePath: this.absolutePath,
  };
  activeTab = 'info';
  loadFeatures = false;
  public tabsIDs = {
    info: 'infoTab',
    permissions: 'permissionsTab',
    features: 'features',
    attachedElements: 'attachedElements',
    changeHistory: 'changeHistory',
  };
  currentDamVersion: DamItemVersion;

  damRelatedCollection: DamRelatedCollection[];
  permissions: Permission = null;
  write = false;
  create = false;
  delete = false;
  administration = false;
  damItemVersions: DamItemVersion[] = [];
  fileExtensions: DamItemVersionTypeCountWSelected[] = [];

  constructor(
    protected readonly formConfigService: FormConfigService,
    protected readonly route: ActivatedRoute,
    protected readonly location: Location,
    @Optional() protected readonly windowRef: NbWindowRef,
    protected readonly fileService: FileService,
    protected readonly damService: DamService
  ) {
    super(formConfigService, route, location, windowRef, fileService);
  }

  ngOnInit(): void {
    super.setConfig(this.config);
    super.ngOnInit();
  }

  afterSetModel(): void {
    if (this.model.id) {
      this.loadDamItemVersions();
      this.loadDamItemPermissions();
    }

    this.editName = this.model.header;
    this.catalogBreadCrumbs = this.damService.getCatalogBreadCrumbs(this.model.damCatalogId);
    this.cdr.detectChanges();

  }

  beforeSubmit(): void {
    super.beforeSubmit();

    this.entity.termIDs = 'terms' in this.model && Array.isArray(this.model.terms) ? this.model.terms.map(term => term.id) : [];
    this.entity.damCatalogs = 'damCatalogs' in this.model && Array.isArray(this.model.damCatalogs) ? this.model.damCatalogs.map(element => element.id) : [];
    console.log(this.entity)
  }

  onSubmit(): void {
    this.loading = true;
    this.beforeSubmit();
    let submitResult: Observable<ApiResponse<string>>;

    if (this.isFormUpload === true) {
      submitResult = this.compoApiService.postForm<any, string>(this.uri + '/update/' + (this.id !== null ? String(this.id) : ''), this.entity);
    }
    else {
      submitResult = this.compoApiService.post<any, string>(this.uri + '/update/' + (this.id !== null ? String(this.id) : ''), this.entity);
    }

    submitResult.pipe(take(1))
      .subscribe(success => this.handleSuccess(success), error => this.handleError(error));
  }

  afterSubmit(): void {
    if (this.redirectToEditPage && this.createdId !== undefined) {
      const url = this.config.absolutePath + '/item/edit/' + this.createdId;

      this.router.navigateByUrl(url);
      if (this.editMode) {
        window.location.reload();
      }
    }
  }

  public download(fileName: string): void {
    this.fileService.downloadUrl('/dam/download/' + fileName)
      .pipe(take(1))
      .subscribe(response => {
        FileService.saveAs(response, fileName);
      });
  }

  public onFeatureModelChanges(isModelChanged: boolean): void {
    this.modelFeaturesChanges = isModelChanged;
  }

  public getTab($event): void {
    if ($event.tabId === this.tabsIDs.features && !this.loadFeatures) {
      this.loadFeatures = true;
    }

    if ($event.tabId !== this.tabsIDs.features && this.activeTab === this.tabsIDs.features && this.modelFeaturesChanges === true) {
      this.activeTab = this.tabsIDs.features;
      this.errorToast(translations.unsavedChanges);
      this.tabsetEl.selectTab(this.featureTabEl);
      return;
    }
    this.activeTab = $event.tabId;

  }

  onVersionSelect(version: DamItemVersion) {
    this.currentDamVersion = version;
  }

  loadDamItemVersions() {
    this.compoApiService
      .get<DamItemVersion[]>('/dam/' + this.model.id + '/versions')
      .pipe(take(1))
      .subscribe(response => {
        this.damItemVersions = response.data;
        if (this.damItemVersions[0]) {
          this.currentDamVersion = this.damItemVersions[0];
        }
        const extensionsMap = new Map<string, DamItemVersionTypeCountWSelected>();
        this.damItemVersions.forEach(version => {
          const mapItem = extensionsMap.get(version?.mimeType) || undefined;
          if (mapItem) {
            extensionsMap.set(version.mimeType, {typeOfFile: version.mimeType, count: mapItem.count + 1, selected: false});
          }
          else {
            extensionsMap.set(version.mimeType, {typeOfFile: version.mimeType, count: 1, selected: false});
          }
        });
        extensionsMap.forEach(mapItem => {
          this.fileExtensions.push(mapItem);
        });
      });
  }

  loadDamItemPermissions() {
    this.compoApiService
      .get<Permission>('/dam/permissions/' + this.model.id)
      .pipe(take(1))
      .subscribe(response => {
        this.permissions = response.data;
        if (this.permissions.myPermissions.indexOf('2') !== -1) {
          this.write = true;
        }
        if (this.permissions.myPermissions.indexOf('4') !== -1) {
          this.create = true;
        }
        if (this.permissions.myPermissions.indexOf('8') !== -1) {
          this.delete = true;
        }
        if (this.permissions.myPermissions.indexOf('16') !== -1) {
          this.administration = true;
        }
        this.cdr.detectChanges();
      });
  }

  protected setModel(model: DamItem): void {
    super.setModel(model);
    if (this.editMode) {
      this.compoApiService
        .get<DamRelatedCollection[]>(this.uri + '/collections/' + this.model.id)
        .pipe(take(1))
        .subscribe(response => {
          this.damRelatedCollection = response.data;
          this.cdr.detectChanges();
        });
    }
  }
}
